import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    timeoutMs: Number,
    heartbeatIntervalMs: Number,
    heartbeatUrl: String,
    logoutUrl: String,
    loginUrl: String
  }

  connect() {
    this.activityHandler = this.recordActivity.bind(this)
    this.visibilityHandler = this.handleVisibilityChange.bind(this)
    this.logoutStarted = false
    this.lastHeartbeatAt = 0

    this.boundEvents().forEach((eventName) => {
      window.addEventListener(eventName, this.activityHandler, { passive: true })
    })
    document.addEventListener("visibilitychange", this.visibilityHandler)

    this.recordActivity()
  }

  disconnect() {
    this.boundEvents().forEach((eventName) => {
      window.removeEventListener(eventName, this.activityHandler, { passive: true })
    })
    document.removeEventListener("visibilitychange", this.visibilityHandler)
    this.clearTimers()
  }

  recordActivity() {
    if (this.logoutStarted) return

    this.scheduleLogout()
    this.maybeHeartbeat()
  }

  handleVisibilityChange() {
    if (document.visibilityState === "visible") {
      this.recordActivity()
    }
  }

  boundEvents() {
    return ["pointerdown", "keydown", "touchstart", "scroll", "mousemove"]
  }

  scheduleLogout() {
    this.clearLogoutTimer()
    this.logoutTimer = window.setTimeout(() => this.forceLogout(), this.timeoutMsValue || 900000)
  }

  clearTimers() {
    this.clearLogoutTimer()
  }

  clearLogoutTimer() {
    if (this.logoutTimer) {
      window.clearTimeout(this.logoutTimer)
      this.logoutTimer = null
    }
  }

  maybeHeartbeat() {
    const now = Date.now()
    const interval = this.heartbeatIntervalMsValue || 60000

    if ((now - this.lastHeartbeatAt) < interval) return

    this.lastHeartbeatAt = now
    this.sendHeartbeat()
  }

  async sendHeartbeat() {
    try {
      const response = await fetch(this.heartbeatUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          "Accept": "application/json"
        }
      })

      if (response.status === 401 || response.status === 403) {
        this.forceLogout()
      }
    } catch (_error) {
    }
  }

  async forceLogout() {
    if (this.logoutStarted) return

    this.logoutStarted = true
    this.clearTimers()

    try {
      const response = await fetch(this.logoutUrlValue, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        const payload = await response.json()
        const nextUrl = this.safeGlobalLogoutUrl(payload?.next_url)
        if (nextUrl) {
          window.location.assign(nextUrl)
          return
        }
      }
    } catch (_error) {
    }

    window.location.assign(this.sessionRecoveryUrl())
  }

  safeGlobalLogoutUrl(value) {
    try {
      const url = new URL(value)
      const allowedHosts = ["fm.313.cash", "solar.313.cash", "www.313.cash", "313.cash"]
      if (url.protocol !== "https:") return null
      if (!allowedHosts.includes(url.hostname)) return null
      if (url.port) return null
      if (url.pathname !== "/logout/relay") return null
      if (!url.searchParams.has("ticket")) return null
      if (!url.searchParams.has("target_host")) return null
      if (!url.searchParams.has("stage")) return null

      return url.toString()
    } catch (_error) {
      return null
    }
  }

  sessionRecoveryUrl() {
    const url = new URL(this.loginUrlValue || "/login", window.location.origin)
    url.searchParams.set("session_expired", "1")
    return url.toString()
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
